import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { betaApi, getBetaToken, setBetaToken, clearBetaToken, initBetaInterceptors } from "./betaClient";

const Ctx = createContext(null);
// The no-provider fallback. `registered: true` matches the real predicate
// (`!enforced || !!user`): with no provider there is no enforcement, so nobody
// is unregistered. Without it, App.js's registration interrupt would read
// undefined and fire for every caller that mounts App outside a BetaProvider.
export const useBeta = () => useContext(Ctx) || { enforced: false, status: "ready", registered: true };

export function BetaProvider({ children }) {
  const [enforced, setEnforced] = useState(false);
  const [status, setStatus] = useState("booting"); // booting | gate | suspended | ready
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const [devices, setDevices] = useState([]);
  const [currentDeviceId, setCurrentDeviceId] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);

  const loadMe = useCallback(async () => {
    const d = await betaApi.me();
    setUser(d.user);
    setUsage(d.usage);
    setDevices(d.devices || []);
    setCurrentDeviceId(d.current_device_id);
    setStatus("ready");
  }, []);

  const boot = useCallback(async () => {
    initBetaInterceptors();
    let cfg;
    try {
      cfg = await betaApi.config();
    } catch {
      setEnforced(false);
      setStatus("ready"); // fail-open: never lock a tester out on a config hiccup
      return;
    }
    const on = !!cfg.enforced;
    setEnforced(on);
    if (!on) {
      setStatus("ready");
      return;
    }
    if (!getBetaToken()) {
      setStatus("gate");
      return;
    }
    try {
      await loadMe();
    } catch (e) {
      if (e?.response?.status === 403) setStatus("suspended");
      else {
        clearBetaToken();
        setStatus("gate");
      }
    }
  }, [loadMe]);

  useEffect(() => {
    boot();
  }, [boot]);

  const refreshUsage = useCallback(async () => {
    try {
      setUsage(await betaApi.usage());
    } catch {
      /* ignore */
    }
  }, []);

  const refreshDevices = useCallback(async () => {
    try {
      const d = await betaApi.devices();
      setDevices(d.devices || []);
      setCurrentDeviceId(d.current_device_id);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onUsage = () => refreshUsage();
    const onLimit = (e) => {
      setLimitInfo(e.detail || {});
      refreshUsage();
    };
    const onEnded = () => {
      clearBetaToken();
      setUser(null);
      setEnforced((cur) => {
        if (cur) setStatus("gate");
        return cur;
      });
    };
    const onSusp = () => setEnforced((cur) => {
      if (cur) setStatus("suspended");
      return cur;
    });
    window.addEventListener("beta:usage-changed", onUsage);
    window.addEventListener("beta:limit", onLimit);
    window.addEventListener("beta:session-ended", onEnded);
    window.addEventListener("beta:suspended", onSusp);
    return () => {
      window.removeEventListener("beta:usage-changed", onUsage);
      window.removeEventListener("beta:limit", onLimit);
      window.removeEventListener("beta:session-ended", onEnded);
      window.removeEventListener("beta:suspended", onSusp);
    };
  }, [refreshUsage]);

  const redeem = useCallback(async (code, email, deviceName) => {
    const d = await betaApi.redeem({ code, email: email || undefined, device_name: deviceName || undefined });
    setBetaToken(d.token);
    setUser(d.user);
    setUsage(d.usage);
    await loadMe();
    return d;
  }, [loadMe]);

  // Batch 121 — the public door. Sends the link; mints nothing.
  const signup = useCallback(async (email) => betaApi.signup({ email }), []);

  // Batch 121 — claiming a verification link.
  //
  // THE LIVE-SESSION CHECK COMES FIRST, and it is the whole of the "second click"
  // ruling. Someone re-opening the link on the device they already used clicked it
  // because they wanted to be in the app, not to read an error. If a token is
  // present and /beta/me accepts it, we let them in and NEVER present the spent
  // token to the server -- so the "this link has already been used" copy only ever
  // reaches someone who genuinely has no session.
  // Batch 122 — email AND code. The link and its second-click rule are gone: a
  // code is typed into a card inside the app, so there is no re-opened URL to
  // handle and no live-session check to make first.
  const verifySignup = useCallback(async (email, code) => {
    const d = await betaApi.verify({ email, code });
    setBetaToken(d.token);
    setUser(d.user);
    setUsage(d.usage);
    await loadMe();
    return d;
  }, [loadMe]);

  // Batch 122 — the app now MOUNTS for an unregistered user, so "is this person
  // registered" stops being answerable from `status` (which is "gate" for them,
  // and renders the app just like "ready"). This is the one predicate the
  // interrupt reads.
  const registered = !enforced || !!user;

  const logout = useCallback(() => {
    clearBetaToken();
    setUser(null);
    setUsage(null);
    setDevices([]);
    if (enforced) setStatus("gate");
  }, [enforced]);

  const revokeDevice = useCallback(async (id) => {
    await betaApi.revokeDevice(id);
    await refreshDevices();
  }, [refreshDevices]);

  // Batch 127 — deliberately does NOT call refreshDevices: the caller has no
  // session, so /beta/devices would 401. The card updates its own list from the
  // 409 body it already holds.
  const revokeWithCode = useCallback(
    async (email, code, deviceId) => betaApi.revokeWithCode({ email, code, device_id: deviceId }),
    []);

  const value = {
    enforced, status, user, usage, devices, currentDeviceId,
    limitInfo, setLimitInfo, redeem, signup, verifySignup, logout, revokeDevice,
    revokeWithCode, refreshUsage, refreshDevices, registered,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
